/**
 * Flow Optimizer formula — Tokenizer
 * For Media Devoted | Created By Ciro Autiero
 *
 * Splits an expression source string into a flat token stream that the
 * recursive-descent parser consumes. No regex `eval`, no `Function`, no
 * member access. Anything outside the whitelist throws a parse error.
 */

export type TokenType =
  | "number"
  | "ident"
  | "lparen"
  | "rparen"
  | "comma"
  | "op"
  | "eof";

export interface Token {
  type: TokenType;
  value: string;
  position: number;
}

const SINGLE_CHAR_OPS = new Set(["+", "-", "*", "/", "%"]);

export function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let index = 0;
  while (index < source.length) {
    const char = source[index];
    if (isWhitespace(char)) {
      index++;
      continue;
    }
    if (isDigit(char) || (char === "." && isDigit(source[index + 1] ?? ""))) {
      const consumed = readNumber(source, index);
      tokens.push({ type: "number", value: consumed.value, position: index });
      index += consumed.length;
      continue;
    }
    if (isIdentStart(char)) {
      const consumed = readIdent(source, index);
      tokens.push({ type: "ident", value: consumed.value, position: index });
      index += consumed.length;
      continue;
    }
    const single = readSingle(char, index);
    if (single) {
      tokens.push(single);
      index++;
      continue;
    }
    throw parseError(index, `Unexpected character '${char}'`);
  }
  tokens.push({ type: "eof", value: "", position: index });
  return tokens;
}

function readNumber(
  source: string,
  start: number,
): { value: string; length: number } {
  let i = start;
  let sawDot = false;
  while (i < source.length) {
    const c = source[i];
    if (isDigit(c)) {
      i++;
      continue;
    }
    if (c === "." && !sawDot) {
      sawDot = true;
      i++;
      continue;
    }
    break;
  }
  return { value: source.slice(start, i), length: i - start };
}

function readIdent(
  source: string,
  start: number,
): { value: string; length: number } {
  let i = start;
  while (i < source.length && isIdentPart(source[i])) i++;
  return { value: source.slice(start, i), length: i - start };
}

function readSingle(char: string, position: number): Token | null {
  if (char === "(") return { type: "lparen", value: char, position };
  if (char === ")") return { type: "rparen", value: char, position };
  if (char === ",") return { type: "comma", value: char, position };
  if (SINGLE_CHAR_OPS.has(char)) return { type: "op", value: char, position };
  return null;
}

function isWhitespace(c: string): boolean {
  return c === " " || c === "\t" || c === "\n" || c === "\r";
}

function isDigit(c: string): boolean {
  return c >= "0" && c <= "9";
}

function isIdentStart(c: string): boolean {
  return (c >= "a" && c <= "z") || (c >= "A" && c <= "Z") || c === "_";
}

function isIdentPart(c: string): boolean {
  return isIdentStart(c) || isDigit(c);
}

function parseError(position: number, message: string): Error {
  const err = new Error(`Formula parse error at ${position}: ${message}`);
  (err as Error & { position?: number }).position = position;
  return err;
}
